import React, { useState, useEffect } from 'react';
import { schedulePublication, getTask, listProjectCredentials, listSiteProjects, listWebflowSites, PlatformType, ContentType, SiteProjectSummary, WebflowSite } from '../services/api';
import { GlassModal } from './ui/GlassModal';
import { GlassButton } from './ui/GlassButton';
import { GlassSelect } from './ui/GlassSelect';
import { CalendarDays, AlertCircle, Loader2, Globe } from 'lucide-react';

/** Normalize FastAPI error detail — may be a string or a Pydantic validation error array */
const getApiError = (e: any, fallback: string): string => {
  const detail = e?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((d: any) => d?.msg ?? JSON.stringify(d)).join('; ');
  }
  return fallback;
};


interface SchedulePublicationModalProps {
  isOpen: boolean;
  onClose: () => void;
  taskId: string;
  projectId: string;
  onScheduled?: () => void;
}

interface PlatformCredential {
  credential_id: string;
  platform: string;
  status: string;
  last_verified_at: string | null;
}

interface TaskData {
  task_id: string;
  name: string;
  synthesized_result: string | null;
  input_data?: any;
  output_data?: any;
}

// Format Date for <input type="datetime-local"> using LOCAL time components.
// Date.prototype.toISOString() always returns UTC, which would silently shift
// the displayed default by the browser's UTC offset (visible as "wrong" times
// in the picker). datetime-local needs the user's local wall-clock value.
const toLocalDateTimeInputValue = (d: Date): string => {
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T` +
    `${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
};


const SchedulePublicationModal: React.FC<SchedulePublicationModalProps> = ({
  isOpen,
  onClose,
  taskId,
  projectId,
  onScheduled
}) => {
  const [loading, setLoading] = useState(false);
  const [taskData, setTaskData] = useState<TaskData | null>(null);
  const [credentials, setCredentials] = useState<PlatformCredential[]>([]);
  const [siteProjects, setSiteProjects] = useState<SiteProjectSummary[]>([]);
  const [webflowSites, setWebflowSites] = useState<WebflowSite[]>([]);
  const [selectedPlatform, setSelectedPlatform] = useState<string>('');
  // AiMe sites: set of site_project_ids selected for publish
  const [selectedSiteIds, setSelectedSiteIds] = useState<Set<string>>(new Set());
  // Webflow sites: set of webflow_site_ids selected for publish
  const [selectedWebflowSiteIds, setSelectedWebflowSiteIds] = useState<Set<string>>(new Set());
  const [scheduledFor, setScheduledFor] = useState<string>(() => {
    const date = new Date();
    date.setHours(date.getHours() + 1);
    return toLocalDateTimeInputValue(date);
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Non-blocking SEO pacing advisory from the backend (publication IS
  // scheduled). When set, the modal stays open to show it — footer becomes
  // Close-only so the user can't double-schedule.
  const [pacingWarning, setPacingWarning] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      loadTaskData();
      loadCredentials();
      loadSites();
      loadWebflowSites();
      setSelectedSiteIds(new Set());
      setSelectedWebflowSiteIds(new Set());
      setPacingWarning(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, taskId, projectId]);

  const loadTaskData = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getTask(taskId);
      setTaskData(data);
    } catch (err: any) {
      console.error('Failed to load task data:', err);
      setError('Failed to load content data');
    } finally {
      setLoading(false);
    }
  };

  const loadCredentials = async () => {
    try {
      const creds = await listProjectCredentials(projectId);
      setCredentials(creds.filter((c: PlatformCredential) => c.status === 'active'));
      const wordpressCred = creds.find((c: PlatformCredential) => c.platform === 'wordpress' && c.status === 'active');
      if (wordpressCred) setSelectedPlatform('wordpress');
    } catch (err: any) {
      console.error('Failed to load credentials:', err);
    }
  };

  const loadSites = async () => {
    try {
      const sites = await listSiteProjects(projectId);
      setSiteProjects(sites.filter(s => s.status === 'published' || s.status === 'draft'));
    } catch { /* non-fatal */ }
  };

  const loadWebflowSites = async () => {
    // Webflow is productised — every authenticated integrations-module user can
    // list their own sites. Visibility of the Webflow target group still gates
    // on webflowSites.filter(is_fully_configured).length > 0 (see render block),
    // so users without a connected site see nothing.
    try {
      const data = await listWebflowSites(projectId);
      setWebflowSites(data.sites);
    } catch { /* non-fatal */ }
  };

  const toggleSite = (id: string) => {
    setSelectedSiteIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const determineContentType = (): ContentType => {
    if (!taskData) return 'article';
    if (taskData.input_data?.quick_win_type === 'social_media_posts') return 'social_post';
    return 'article';
  };

  const extractContentData = (): any => {
    if (!taskData) return {};

    const contentType = determineContentType();

    if (contentType === 'article') {
      // Strip legacy prompt cruft AND the orchestrator's content-type label
      // ("Article: …", "Blog Post - …") — unnecessary on a published blog.
      // Separator required right after the label so titles like
      // "Article 9 Compliance: A Guide" are untouched.
      const title = taskData.name
        .replace('Generate article:', '')
        .replace(/^\s*(article|blog post|blog)\s*[:\-–]\s*/i, '')
        .trim() || taskData.name.trim();
      const body = taskData.synthesized_result || '';
      const output = taskData.output_data || {};

      const data: any = {
        title,
        body,
        status: 'future',
        categories: output.categories || [],
        tags: output.tags || [],
      };

      if (output.seo_metadata) {
        const seo: any = {
          title: output.seo_metadata.meta_title || title,
        };
        if (output.seo_metadata.meta_description) {
          seo.description = output.seo_metadata.meta_description;
        }
        if (output.seo_metadata.focus_keyword) {
          seo.focus_keyword = output.seo_metadata.focus_keyword;
        }
        if (Object.keys(seo).length > 0) {
          data.seo = seo;
        }
      }

      const hero = output.images?.hero;
      if (hero?.urls?.large) {
        data.featured_image_url = hero.urls.large;
        data.featured_image_alt = title;
        const slug = output.seo_metadata?.slug || title.toLowerCase().replace(/[^a-z0-9]+/g, '-');
        data.featured_image_title = slug;
        if (hero.photographer) {
          data.featured_image_caption = hero.pexels_url
            ? `Photo by ${hero.photographer} on <a href="${hero.pexels_url}" target="_blank" rel="noopener">Pexels</a>`
            : `Photo by ${hero.photographer} on Pexels`;
        }
      }

      return data;
    }

    if (contentType === 'social_post') {
      return { content: taskData.synthesized_result || '', platform_specific: {} };
    }

    return { content: taskData.synthesized_result || '' };
  };

  const totalTargets = (selectedPlatform ? 1 : 0) + selectedSiteIds.size + selectedWebflowSiteIds.size;

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();

    if (!selectedPlatform && selectedSiteIds.size === 0 && selectedWebflowSiteIds.size === 0) {
      setError('Select at least one publish destination');
      return;
    }

    setSubmitting(true);
    setError(null);

    const scheduledForISO = new Date(scheduledFor).toISOString();
    const contentType = determineContentType();
    const contentData = extractContentData();
    const errors: string[] = [];
    let lastPacingWarning: string | null = null;

    try {
      // Standard platform (WordPress etc.)
      if (selectedPlatform) {
        try {
          const pub = await schedulePublication({
            project_id: projectId,
            task_id: taskId,
            platform: selectedPlatform as PlatformType,
            content_type: contentType,
            scheduled_for: scheduledForISO,
            content_data: contentData,
          });
          if (pub.pacing_warning) lastPacingWarning = pub.pacing_warning;
        } catch (err: any) {
          errors.push(`${selectedPlatform}: ${getApiError(err, 'Failed')}`);
        }
      }

      // AiMe Sites — one publication per selected site
      for (const siteId of Array.from(selectedSiteIds)) {
        try {
          const pub = await schedulePublication({
            project_id: projectId,
            task_id: taskId,
            platform: 'aime_site' as PlatformType,
            platform_target_id: siteId,
            content_type: 'article',
            scheduled_for: scheduledForISO,
            content_data: {
              site_project_id: siteId,
              task_id: taskId,
              title_override: contentData.title,
            },
          });
          if (pub.pacing_warning) lastPacingWarning = pub.pacing_warning;
        } catch (err: any) {
          const siteName = siteProjects.find(s => s.site_project_id === siteId)?.name || siteId;
          errors.push(`${siteName}: ${getApiError(err, 'Failed')}`);
        }
      }

      // Webflow CMS — one publication per selected site
      for (const webflowSiteId of Array.from(selectedWebflowSiteIds)) {
        try {
          const pub = await schedulePublication({
            project_id: projectId,
            task_id: taskId,
            platform: 'webflow' as PlatformType,
            platform_target_id: webflowSiteId,
            content_type: 'article',
            scheduled_for: scheduledForISO,
            content_data: contentData,
          });
          if (pub.pacing_warning) lastPacingWarning = pub.pacing_warning;
        } catch (err: any) {
          const wfSite = webflowSites.find(s => s.webflow_site_id === webflowSiteId);
          const siteName = wfSite?.site_display_name || webflowSiteId;
          errors.push(`Webflow (${siteName}): ${getApiError(err, 'Failed')}`);
        }
      }

      if (errors.length > 0) {
        setError(`Some targets failed: ${errors.join('; ')}`);
      } else {
        if (onScheduled) onScheduled();
        if (lastPacingWarning) {
          // Scheduled successfully — keep the modal open to show the pacing
          // advisory; footer switches to Close-only (see render).
          setPacingWarning(lastPacingWarning);
        } else {
          onClose();
        }
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Local timezone label for the user — same source FullCalendar uses for display
  const tzLabel = (() => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || 'local time';
    } catch {
      return 'local time';
    }
  })();

  const platformOptions = credentials.map(c => ({
    value: c.platform,
    label: c.platform.charAt(0).toUpperCase() + c.platform.slice(1),
  }));

  // Theme-aware native input style — datetime-local has no Glass primitive
  const dtInputStyle: React.CSSProperties = {
    width: '100%',
    padding: '0.55rem 0.75rem',
    border: '1px solid var(--stroke)',
    borderRadius: 'var(--radius-md)',
    background: 'var(--panel)',
    color: 'var(--text)',
    fontSize: '0.95rem',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
    colorScheme: 'light dark',
  };

  const labelStyle: React.CSSProperties = {
    display: 'block',
    fontWeight: 600,
    fontSize: '0.875rem',
    color: 'var(--text)',
    marginBottom: '0.35rem',
  };

  const helpStyle: React.CSSProperties = {
    display: 'block',
    fontSize: '0.75rem',
    color: 'var(--text-secondary)',
    marginTop: '0.25rem',
  };

  const titleNode = (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
      <CalendarDays size={20} /> Schedule Publication
    </span>
  );

  return (
    <GlassModal
      isOpen={isOpen}
      onClose={onClose}
      title={titleNode as unknown as string}
      size="md"
      footer={
        pacingWarning ? (
          <GlassButton variant="secondary" onClick={onClose}>
            Close
          </GlassButton>
        ) : (
          <>
            <GlassButton variant="secondary" onClick={onClose} disabled={submitting}>
              Cancel
            </GlassButton>
            <GlassButton
              variant="primary"
              onClick={() => handleSubmit()}
              disabled={submitting || totalTargets === 0}
            >
              {submitting
                ? 'Scheduling…'
                : totalTargets > 1
                  ? `Schedule to ${totalTargets} destinations`
                  : 'Schedule Publication'}
            </GlassButton>
          </>
        )
      }
    >
      {loading && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-secondary)' }}>
          <Loader2 size={16} className="spin" /> Loading content data…
        </div>
      )}

      {error && (
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 8,
            background: 'rgba(239,68,68,0.1)',
            border: '1px solid rgba(239,68,68,0.3)',
            borderRadius: 'var(--radius-md)',
            padding: '10px 12px',
            color: '#ef4444',
            fontSize: '0.875rem',
            marginBottom: '1rem',
          }}
        >
          <AlertCircle size={16} style={{ flexShrink: 0, marginTop: 2 }} />
          <span>{error}</span>
        </div>
      )}

      {pacingWarning && (
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 8,
            background: 'var(--color-warning-soft)',
            border: '1px solid var(--color-warning)',
            borderRadius: 'var(--radius-md)',
            padding: '10px 12px',
            color: 'var(--color-warning-dark)',
            fontSize: '0.875rem',
            marginBottom: '1rem',
          }}
        >
          <AlertCircle size={16} style={{ flexShrink: 0, marginTop: 2 }} />
          <span>
            <strong>Scheduled.</strong> {pacingWarning}
          </span>
        </div>
      )}

      {!loading && taskData && (
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '1.25rem' }}>
            <label style={labelStyle}>Content</label>
            <div
              style={{
                background: 'var(--panel-strong)',
                border: '1px solid var(--stroke)',
                borderRadius: 'var(--radius-md)',
                padding: '12px 14px',
              }}
            >
              <strong style={{ color: 'var(--text)', display: 'block', marginBottom: 4 }}>
                {taskData.name.replace('Generate article:', '').trim()}
              </strong>
              <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: '0.875rem', lineHeight: 1.5 }}>
                {(taskData.synthesized_result || '').substring(0, 200)}…
              </p>
            </div>
          </div>

          <div style={{ marginBottom: '1.25rem' }}>
            <label style={labelStyle}>Platform *</label>
            {credentials.length === 0 ? (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 8,
                  background: 'rgba(245,158,11,0.1)',
                  border: '1px solid rgba(245,158,11,0.3)',
                  borderRadius: 'var(--radius-md)',
                  padding: '10px 12px',
                  color: '#d97706',
                  fontSize: '0.875rem',
                }}
              >
                <AlertCircle size={16} style={{ flexShrink: 0, marginTop: 2 }} />
                <span>No platform credentials configured. Add credentials in the Scheduling page first.</span>
              </div>
            ) : (
              <GlassSelect
                options={platformOptions}
                value={selectedPlatform}
                onChange={setSelectedPlatform}
                placeholder="Select platform…"
              />
            )}
          </div>

          {siteProjects.length > 0 && (
            <div style={{ marginBottom: '1.25rem' }}>
              <label style={labelStyle}>
                <Globe size={14} style={{ verticalAlign: 'middle', marginRight: 5 }} />
                AiMe Sites
              </label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {siteProjects.map(site => {
                  // Imported static sites can only receive a blog publication once they have a
                  // blog_index generated page (_assert_imported_site_has_blog backend gate).
                  // Disable rather than hide, so the site's absence is explained rather than
                  // mysterious — and point the user at the fix instead of a dead end.
                  const isImported = (site.source_type || 'component') === 'imported_static';
                  const needsBlogPage = isImported && !site.has_blog_index;
                  return (
                  <label
                    key={site.site_project_id}
                    title={needsBlogPage ? 'No blog page — create one in the Imported Site Editor' : undefined}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      padding: '8px 12px',
                      background: selectedSiteIds.has(site.site_project_id) ? 'rgba(33,119,255,0.06)' : 'var(--panel-strong)',
                      border: `1px solid ${selectedSiteIds.has(site.site_project_id) ? 'var(--primary-500)' : 'var(--stroke)'}`,
                      borderRadius: 'var(--radius-md)',
                      cursor: needsBlogPage ? 'not-allowed' : 'pointer',
                      userSelect: 'none',
                      opacity: needsBlogPage ? 0.55 : 1,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={selectedSiteIds.has(site.site_project_id)}
                      onChange={() => toggleSite(site.site_project_id)}
                      disabled={needsBlogPage}
                      style={{ accentColor: 'var(--primary-500)' }}
                    />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: '0.875rem', color: 'var(--text)' }}>{site.name}</div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                        {needsBlogPage
                          ? 'No blog page — create one in the Imported Site Editor'
                          : `${site.page_count} page${site.page_count !== 1 ? 's' : ''} · ${site.status}`}
                      </div>
                    </div>
                  </label>
                  );
                })}
              </div>
            </div>
          )}

          {webflowSites.filter(s => s.is_fully_configured).length > 0 && (
            <div style={{ marginBottom: '1.25rem' }}>
              <label style={labelStyle}>
                <Globe size={14} style={{ verticalAlign: 'middle', marginRight: 5 }} />
                Webflow CMS
              </label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {webflowSites.filter(s => s.is_fully_configured).map(site => (
                  <label
                    key={site.webflow_site_id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      padding: '8px 12px',
                      background: selectedWebflowSiteIds.has(site.webflow_site_id) ? 'rgba(33,119,255,0.06)' : 'var(--panel-strong)',
                      border: `1px solid ${selectedWebflowSiteIds.has(site.webflow_site_id) ? 'var(--primary-500)' : 'var(--stroke)'}`,
                      borderRadius: 'var(--radius-md)',
                      cursor: 'pointer',
                      userSelect: 'none',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={selectedWebflowSiteIds.has(site.webflow_site_id)}
                      onChange={e => {
                        setSelectedWebflowSiteIds(prev => {
                          const next = new Set(prev);
                          e.target.checked ? next.add(site.webflow_site_id) : next.delete(site.webflow_site_id);
                          return next;
                        });
                      }}
                      style={{ accentColor: 'var(--primary-500)' }}
                    />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: '0.875rem', color: 'var(--text)' }}>
                        {site.site_display_name || 'Webflow'}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                        {site.blog_collection_name || 'Blog'} · Webflow CMS
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          )}

          <div style={{ marginBottom: '0.5rem' }}>
            <label htmlFor="sched-pub-when" style={labelStyle}>
              Publish Date &amp; Time *
            </label>
            <input
              id="sched-pub-when"
              type="datetime-local"
              value={scheduledFor}
              onChange={(e) => setScheduledFor(e.target.value)}
              min={toLocalDateTimeInputValue(new Date())}
              required
              style={dtInputStyle}
            />
            <span style={helpStyle}>
              Times shown in your local timezone ({tzLabel}). Content will publish at this time.
            </span>
          </div>
        </form>
      )}

      <style>{`
        .spin { animation: spin 1s linear infinite; }
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </GlassModal>
  );
};

export default SchedulePublicationModal;
